import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { MessageSquareText, X, Phone, Headset } from "lucide-react";
import { useI18n } from "@/i18n";
import { CONTACT } from "@/lib/data";

const SMS_HREF = `sms:+13056911516?&body=${encodeURIComponent(
  "Hi EWS! I'd like a fast quote for commercial waste / dumpster service. My business is:"
)}`;

export default function FloatingTextCTA() {
  const { pathname } = useLocation();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowBubble(true), 2600);
    return () => clearTimeout(timer);
  }, [pathname]);
  useEffect(() => { setOpen(false); }, [pathname]);

  if (pathname.startsWith("/admin")) return null;

  const optionBase =
    "group inline-flex items-center gap-2.5 pl-3 pr-5 py-3 rounded-full font-bold shadow-xl w-full justify-start";

  return (
    <div className="fixed bottom-5 right-5 sm:bottom-7 sm:right-7 z-[60] hidden sm:flex flex-col items-end gap-3">
      {/* Prompt bubble (only before the menu is opened) */}
      <AnimatePresence>
        {showBubble && !dismissed && !open && (
          <motion.button
            onClick={() => { setOpen(true); setDismissed(true); }}
            initial={{ opacity: 0, y: 12, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="relative hidden sm:block text-left max-w-[250px] bg-white border border-[#E7E5DF] rounded-2xl rounded-br-md shadow-2xl p-4 pr-8"
            data-testid="text-cta-bubble"
          >
            <span onClick={(e) => { e.stopPropagation(); setDismissed(true); }} aria-label="Dismiss"
              className="absolute top-2.5 right-2.5 text-slate-400 hover:text-slate-700 transition-colors">
              <X size={15} />
            </span>
            <p className="text-sm text-[#0B0F14] leading-snug">
              <span className="font-bold">{t("text.bubbleBold")}</span>{t("text.bubble")}
            </p>
            <span className="absolute -bottom-[7px] right-5 w-3.5 h-3.5 bg-white border-b border-r border-[#E7E5DF] rotate-45" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Expanded options: Call + Text */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="flex flex-col items-end gap-2.5 w-[188px]"
            initial="hidden" animate="visible" exit="hidden"
            variants={{ visible: { transition: { staggerChildren: 0.06 } }, hidden: {} }}
          >
            <motion.a
              href={CONTACT.phoneHref}
              data-testid="floating-call-cta"
              variants={{ hidden: { opacity: 0, y: 10, scale: 0.9 }, visible: { opacity: 1, y: 0, scale: 1 } }}
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.96 }}
              className={`${optionBase} bg-[#0E3B3B] text-white`}
            >
              <span className="grid place-items-center w-8 h-8 rounded-full bg-emerald-500 text-[#0A2C2C]"><Phone size={16} /></span>
              <span className="flex flex-col leading-tight">
                <span>{t("text.call")}</span>
                <span className="text-[11px] font-medium text-emerald-300">{CONTACT.phone}</span>
              </span>
            </motion.a>
            <motion.a
              href={SMS_HREF}
              data-testid="floating-text-cta"
              variants={{ hidden: { opacity: 0, y: 10, scale: 0.9 }, visible: { opacity: 1, y: 0, scale: 1 } }}
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.96 }}
              className={`${optionBase} bg-white text-[#0B0F14] border border-[#E7E5DF]`}
            >
              <span className="grid place-items-center w-8 h-8 rounded-full bg-emerald-500/15 text-emerald-700"><MessageSquareText size={16} /></span>
              <span className="flex flex-col leading-tight">
                <span>{t("text.us")}</span>
                <span className="text-[11px] font-medium text-slate-400">{t("text.fast")}</span>
              </span>
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Single main button */}
      <motion.button
        onClick={() => { setOpen((v) => !v); setDismissed(true); }}
        data-testid="floating-contact-fab"
        aria-label={t("cta.contact")}
        aria-expanded={open}
        initial={{ opacity: 0, scale: 0, rotate: -25 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ delay: 0.9, type: "spring", stiffness: 200, damping: 14 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="group relative inline-flex items-center gap-2.5 pl-3.5 pr-5 py-3.5 rounded-full bg-emerald-500 text-[#0A2C2C] font-bold shadow-[0_12px_40px_-8px_rgba(16,185,129,0.65)]"
      >
        {!open && <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-25" />}
        <span className="relative grid place-items-center w-8 h-8 rounded-full bg-[#0A2C2C] text-emerald-400">
          <AnimatePresence mode="wait" initial={false}>
            {open ? (
              <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
                <X size={17} />
              </motion.span>
            ) : (
              <motion.span key="h" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
                <Headset size={17} />
              </motion.span>
            )}
          </AnimatePresence>
        </span>
        <span className="relative">{t("cta.contact")}</span>
      </motion.button>
    </div>
  );
}
